import React from 'react'
import clsx from 'clsx'
import { CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import MediaAvatar from '../../MediaAvatar'

import { isUUID } from '../../../../utilities'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import { useEnvironment } from '../../../../providers/EnvironmentProvider'
import useStyles from './styles'

type TagProps = {
  disabled: boolean
  'data-tag-index': number
  tabIndex: -1
  className: string
  option: string
  onClick: React.HTMLAttributes<SVGElement>['onClick']
  markSelf: boolean
}

const Tag: React.FC<TagProps> = ({
  option,
  className,
  onClick,
  markSelf,
  ...props
}) => {
  const theme = useTheme()

  const { allUsers } = useUserAPI()
  const { user: currentUser } = useEnvironment()

  const isNew = React.useMemo(() => !isUUID(option), [option])

  const user = React.useMemo(() => allUsers[option], [allUsers, option])

  const name = React.useMemo(() => {
    if (isNew) {
      return (
        <>
          <span className='has-suffix'>{option}</span> (new user)
        </>
      )
    }

    if (!user) {
      return <>Loading...</>
    }

    if (markSelf && user.id === currentUser?.id) {
      return (
        <>
          <span className='has-suffix'>{user.name}</span> (self)
        </>
      )
    }

    return <span>{user.name}</span>
  }, [isNew, option, user, markSelf, currentUser])

  const classes = useStyles()

  return (
    <div {...props} className={clsx(className, classes.root)}>
      <MediaAvatar user={user ?? { name }} className={classes.avatar} />

      <div className={classes.name}>{name}</div>

      <CloseCircle
        size={theme.size.base}
        onClick={onClick}
        className={classes.close}
      />
    </div>
  )
}

export default Tag
